import type { CSSProperties } from "react";

export type OcrHighlightItem = {
  topLeft?: number[];
  bottomRight?: number[];
  pageNo?: number;
  content?: string;
};

export type BoundingRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type HighlightPageMetrics = {
  originLeft: number;
  originTop: number;
  width: number;
  height: number;
};

export type HighlightPageSize = {
  width: number;
  height: number;
};

const toNumber = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

export const getBoundingRectCoordinates = (
  item: OcrHighlightItem,
  canvasWidth: number,
  canvasHeight: number,
  effectiveRotation: number
): BoundingRect => {
  const topLeftX = toNumber(item.topLeft?.[0]);
  const topLeftY = toNumber(item.topLeft?.[1]);
  const bottomRightX = toNumber(item.bottomRight?.[0]);
  const bottomRightY = toNumber(item.bottomRight?.[1]);

  switch (effectiveRotation) {
    case 90:
      return {
        left: canvasWidth - bottomRightY * canvasWidth,
        top: topLeftX * canvasHeight,
        width: (bottomRightY - topLeftY) * canvasWidth,
        height: (bottomRightX - topLeftX) * canvasHeight
      };
    case 180:
      return {
        left: canvasWidth - bottomRightX * canvasWidth,
        top: canvasHeight - bottomRightY * canvasHeight,
        width: (bottomRightX - topLeftX) * canvasWidth,
        height: (bottomRightY - topLeftY) * canvasHeight
      };
    case 270:
      return {
        left: topLeftY * canvasWidth,
        top: canvasHeight - bottomRightX * canvasHeight,
        width: (bottomRightY - topLeftY) * canvasWidth,
        height: (bottomRightX - topLeftX) * canvasHeight
      };
    default:
      return {
        left: topLeftX * canvasWidth,
        top: topLeftY * canvasHeight,
        width: (bottomRightX - topLeftX) * canvasWidth,
        height: (bottomRightY - topLeftY) * canvasHeight
      };
  }
};

export const getHighlightPageMetrics = (
  wrap: HTMLElement | null | undefined,
  canvas: HTMLElement | null | undefined,
  fallback?: HighlightPageSize
): HighlightPageMetrics => {
  const fallbackWidth = fallback?.width ?? 0;
  const fallbackHeight = fallback?.height ?? 0;
  if (!wrap || !canvas) {
    return {
      originLeft: 0,
      originTop: 0,
      width: fallbackWidth,
      height: fallbackHeight
    };
  }

  const wrapRect = wrap.getBoundingClientRect();
  const canvasRect = canvas.getBoundingClientRect();
  return {
    originLeft: canvasRect.left - wrapRect.left,
    originTop: canvasRect.top - wrapRect.top,
    width: canvasRect.width || canvas.clientWidth || fallbackWidth,
    height: canvasRect.height || canvas.clientHeight || fallbackHeight
  };
};

export const getHighlightOverlayStyle = (
  item: OcrHighlightItem,
  metrics: HighlightPageMetrics,
  rotation: number,
  pixelPadding: number
): CSSProperties => {
  const rect = getBoundingRectCoordinates(
    item,
    metrics.width,
    metrics.height,
    rotation
  );
  return {
    position: "absolute",
    left: Math.round(metrics.originLeft + rect.left - pixelPadding),
    top: Math.round(metrics.originTop + rect.top - pixelPadding),
    width: Math.round(rect.width + 2 * pixelPadding),
    height: Math.round(rect.height + 2 * pixelPadding),
    border: "2px solid red",
    boxSizing: "border-box",
    pointerEvents: "none",
    zIndex: 3
  };
};

export const getOcrHighlightKey = (pageNo: number, item: OcrHighlightItem): string =>
  [
    pageNo,
    item.pageNo ?? "",
    item.content ?? "",
    item.topLeft?.join(",") ?? "",
    item.bottomRight?.join(",") ?? ""
  ].join("|");
